import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChartBar, CalendarBlank, Tag, Clock, SignOut, Trash, Check, CheckCircle, CreditCard, Money, Receipt, FileArrowDown } from "@phosphor-icons/react";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["Booked", "Picked Up", "Washing", "Ready", "Out for Delivery", "Delivered"];
const input = "rounded-xl border-2 border-brand-green/15 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-brand-green";

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");

  useEffect(() => { document.title = "Admin Dashboard | Zaekids Laundromat"; }, []);
  useEffect(() => {
    if (user === false) navigate("/login");
    if (user && user.role !== "admin") navigate("/account");
  }, [user, navigate]);

  if (!user || user.role !== "admin") return <div className="py-32 text-center font-bold text-brand-green">Checking access…</div>;

  const tabs = [
    { id: "bookings", label: "Bookings", icon: CalendarBlank },
    { id: "invoices", label: "Invoices", icon: Receipt },
    { id: "services", label: "Services & Prices", icon: Tag },
    { id: "slots", label: "Time Slots", icon: Clock },
    { id: "stats", label: "Stats", icon: ChartBar },
  ];

  return (
    <div data-testid="admin-page" className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-black tracking-tight text-brand-green">Staff Dashboard</h1>
        <button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/"); }}
          className="flex items-center gap-2 rounded-full border-2 border-red-200 px-5 py-2.5 text-sm font-extrabold text-red-600 hover:bg-red-50">
          <SignOut size={18} weight="bold" /> Log Out
        </button>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-extrabold transition-colors duration-200 ${tab === t.id ? "bg-brand-green text-white" : "bg-white text-brand-green shadow-sm"}`}>
            <t.icon size={18} weight="duotone" /> {t.label}
          </button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "bookings" && <BookingsTab />}
        {tab === "invoices" && <InvoicesTab />}
        {tab === "services" && <ServicesTab />}
        {tab === "slots" && <SlotsTab />}
        {tab === "stats" && <StatsTab />}
      </div>
    </div>
  );
}

function BookingsTab() {
  const [bookings, setBookings] = useState([]);
  const [status, setStatus] = useState("");
  const [date, setDate] = useState("");

  const load = useCallback(() => {
    const params = {};
    if (status) params.status = status;
    if (date) params.date = date;
    api.get("/admin/bookings", { params }).then((r) => setBookings(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [status, date]);

  useEffect(load, [load]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const downloadInvoice = async (b) => {
    try {
      const res = await api.get(`/admin/bookings/${b.id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${b.order_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="admin-bookings-tab">
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="filter-status-select" className={input} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input data-testid="filter-date-input" type="date" className={input} value={date} onChange={(e) => setDate(e.target.value)} />
        {(status || date) && <button data-testid="clear-filters-btn" onClick={() => { setStatus(""); setDate(""); }} className="text-sm font-extrabold text-brand-green underline">Clear filters</button>}
      </div>
      <div className="mt-5 overflow-x-auto rounded-3xl bg-white shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="bg-soft-green text-brand-green">
              {["Order", "Customer", "Services", "Pick up", "Address", "Weight (kg)", "Total", "Paid", "Status"].map((h) => (
                <th key={h} className="px-4 py-3 font-extrabold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center font-bold text-slate-400">No bookings found</td></tr>
            )}
            {bookings.map((b) => (
              <tr key={b.id} data-testid={`admin-booking-row-${b.order_number}`} className="border-t border-brand-green/5">
                <td className="px-4 py-3 font-black text-brand-green">{b.order_number}
                  <button data-testid={`download-invoice-${b.order_number}`} onClick={() => downloadInvoice(b)}
                    className="mt-1 flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-brand-green">
                    <FileArrowDown size={13} weight="bold" /> Invoice
                  </button>
                </td>
                <td className="px-4 py-3">{b.name}<br /><span className="text-xs text-slate-400">{b.phone}</span></td>
                <td className="px-4 py-3">{b.services.join(", ")}{b.weight_kg > 0 && ` · ${b.weight_kg}kg`}</td>
                <td className="px-4 py-3">{b.pickup_date}<br /><span className="text-xs text-slate-400">{b.time_slot}</span></td>
                <td className="px-4 py-3 text-xs">{b.street}, {b.suburb} {b.postcode}</td>
                <td className="px-4 py-3"><WeightCell b={b} onSaved={load} /></td>
                <td className="px-4 py-3 font-bold">${b.pricing.total.toFixed(2)}</td>
                <td className="px-4 py-3"><PaymentCell b={b} onSaved={load} /></td>
                <td className="px-4 py-3">
                  <select data-testid={`status-select-${b.order_number}`} className={input} value={b.status}
                    onChange={(e) => updateStatus(b.id, e.target.value)}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WeightCell({ b, onSaved }) {
  const hasKg = ["wash", "dry", "fold"].some((c) => (b.services || []).includes(c));
  const [w, setW] = useState(b.weight_kg || 0);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setW(b.weight_kg || 0); }, [b.weight_kg]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/bookings/${b.id}/weight`, { weight_kg: Number(w) });
      toast.success("Weight saved — total recalculated & customer emailed");
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(false); }
  };

  if (!hasKg) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="flex items-center gap-1">
      <input data-testid={`weight-input-${b.order_number}`} type="number" step="0.1" min="0" value={w}
        onChange={(e) => setW(e.target.value)}
        className="w-16 rounded-lg border-2 border-brand-green/15 px-2 py-1 text-sm font-semibold outline-none focus:border-brand-green" />
      <button data-testid={`save-weight-${b.order_number}`} onClick={save} disabled={saving || Number(w) === (b.weight_kg || 0)}
        className="rounded-lg bg-brand-green px-2.5 py-1.5 text-xs font-extrabold text-white disabled:opacity-40" aria-label="Save weight">
        <Check size={14} weight="bold" />
      </button>
    </div>
  );
}

function PaymentCell({ b, onSaved }) {
  const [busy, setBusy] = useState(false);
  const payCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${b.id}/checkout`, { origin_url: window.location.origin });
      window.open(data.checkout_url, "_blank", "noopener");
      toast.message("Stripe checkout opened in a new tab — reload after payment to refresh status.");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  const payCash = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${b.id}/mark-paid`);
      toast.success("Marked as paid (cash)");
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };
  if (b.payment_status === "paid") {
    return (
      <span data-testid={`paid-badge-${b.order_number}`} className="inline-flex items-center gap-1 rounded-full bg-soft-green px-2.5 py-1 text-xs font-extrabold text-brand-green">
        <CheckCircle size={13} weight="fill" /> Paid{b.payment_method === "cash" ? " (cash)" : ""}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <button data-testid={`pay-card-${b.order_number}`} onClick={payCard} disabled={busy}
        className="flex items-center gap-1 rounded-lg bg-brand-green px-2.5 py-1.5 text-xs font-extrabold text-white disabled:opacity-40">
        <CreditCard size={13} weight="bold" /> Card
      </button>
      <button data-testid={`pay-cash-${b.order_number}`} onClick={payCash} disabled={busy}
        className="flex items-center gap-1 rounded-lg border-2 border-brand-green/30 px-2.5 py-1.5 text-xs font-extrabold text-brand-green disabled:opacity-40">
        <Money size={13} weight="bold" /> Cash
      </button>
    </div>
  );
}

const InvField = ({ label, full, children }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="mb-1 block text-xs font-extrabold text-brand-green">{label}</label>
    {children}
  </div>
);

function InvoicesTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/invoices").then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const download = async (inv) => {
    try {
      const res = await api.get(`/admin/bookings/${inv.booking_id}/invoice`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_number}-${inv.order_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      abn: inv.abn || "", business_address: inv.business_address || "", business_phone: inv.business_phone || "",
      business_email: inv.business_email || "", name: inv.name || "", phone: inv.phone || "", email: inv.email || "",
      discount: inv.discount || 0, gst: inv.gst || 0, payment_method: inv.payment_method || "",
      payment_reference: inv.payment_reference || "", notes_text: inv.notes_text || "",
    });
  };

  const save = async (thenDownload) => {
    setSaving(true);
    try {
      const payload = { ...form, discount: Number(form.discount) || 0, gst: Number(form.gst) || 0 };
      await api.patch(`/admin/bookings/${editing.booking_id}/invoice`, payload);
      toast.success("Invoice updated");
      await load();
      if (thenDownload) await download(editing);
      setEditing(null);
    } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(false); }
  };

  const fi = "w-full rounded-lg border-2 border-brand-green/15 px-3 py-2 text-sm font-semibold outline-none focus:border-brand-green";

  return (
    <div data-testid="admin-invoices-tab">
      <div className="overflow-x-auto rounded-3xl bg-white shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
        <table className="w-full min-w-[820px] text-left text-sm">
          <thead>
            <tr className="bg-soft-green text-brand-green">
              {["Invoice #", "Order", "Customer", "Issued", "Amount", "Status", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-extrabold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center font-bold text-slate-400">No invoices yet — they're created automatically when an order is paid or delivered.</td></tr>
            )}
            {rows.map((inv) => (
              <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_number}`} className="border-t border-brand-green/5">
                <td className="px-4 py-3 font-black text-brand-green">{inv.invoice_number}</td>
                <td className="px-4 py-3">{inv.order_number}</td>
                <td className="px-4 py-3">{inv.name}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(inv.issued_at).toLocaleDateString("en-AU", { dateStyle: "medium" })}</td>
                <td className="px-4 py-3 font-bold">${Number(inv.amount).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-extrabold ${inv.paid ? "bg-soft-green text-brand-green" : "bg-amber-100 text-amber-700"}`}>{inv.paid ? "Paid" : "Due"}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`edit-invoice-${inv.invoice_number}`} onClick={() => openEdit(inv)}
                      className="rounded-lg border-2 border-brand-green/30 px-3 py-1.5 text-xs font-extrabold text-brand-green">Edit</button>
                    <button data-testid={`download-invoice-btn-${inv.invoice_number}`} onClick={() => download(inv)}
                      className="flex items-center gap-1 rounded-lg bg-brand-green px-3 py-1.5 text-xs font-extrabold text-white">
                      <FileArrowDown size={13} weight="bold" /> PDF
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div data-testid="invoice-edit-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-black text-brand-green">Edit {editing.invoice_number}</h3>
            <p className="mb-4 text-xs text-slate-500">Adjust the details, then save &amp; download the PDF.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <InvField label="Business ABN"><input data-testid="inv-abn" className={fi} value={form.abn} onChange={(e) => setForm({ ...form, abn: e.target.value })} /></InvField>
              <InvField label="Business phone"><input data-testid="inv-bphone" className={fi} value={form.business_phone} onChange={(e) => setForm({ ...form, business_phone: e.target.value })} /></InvField>
              <InvField label="Business address" full><input data-testid="inv-baddr" className={fi} value={form.business_address} onChange={(e) => setForm({ ...form, business_address: e.target.value })} /></InvField>
              <InvField label="Customer name"><input data-testid="inv-name" className={fi} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></InvField>
              <InvField label="Customer phone"><input data-testid="inv-phone" className={fi} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></InvField>
              <InvField label="Customer email" full><input data-testid="inv-email" className={fi} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></InvField>
              <InvField label="Discount ($)"><input data-testid="inv-discount" type="number" step="0.01" className={fi} value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} /></InvField>
              <InvField label="GST ($)"><input data-testid="inv-gst" type="number" step="0.01" className={fi} value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></InvField>
              <InvField label="Payment method"><select data-testid="inv-method" className={fi} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}><option value="">—</option><option value="card">EFTPOS / Card</option><option value="bank">Bank Transfer</option><option value="cash">Cash</option><option value="other">Other</option></select></InvField>
              <InvField label="Payment reference"><input data-testid="inv-ref" className={fi} value={form.payment_reference} onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} /></InvField>
              <InvField label="Notes" full><textarea data-testid="inv-notes" className={`${fi} min-h-[70px]`} value={form.notes_text} onChange={(e) => setForm({ ...form, notes_text: e.target.value })} /></InvField>
            </div>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-full border-2 border-slate-200 px-5 py-2.5 text-sm font-extrabold text-slate-500">Cancel</button>
              <button data-testid="invoice-save-btn" onClick={() => save(false)} disabled={saving} className="rounded-full border-2 border-brand-green px-5 py-2.5 text-sm font-extrabold text-brand-green disabled:opacity-50">Save</button>
              <button data-testid="invoice-save-download-btn" onClick={() => save(true)} disabled={saving} className="rounded-full bg-brand-green px-5 py-2.5 text-sm font-extrabold text-white disabled:opacity-50">{saving ? "Saving…" : "Save & Download"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServicesTab() {
  const [data, setData] = useState(null);
  const load = () => api.get("/services").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const save = async (code, price, active) => {
    try {
      await api.put(`/admin/services/${code}`, { price: Number(price), active });
      toast.success("Price updated");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (!data) return <p className="font-bold text-slate-400">Loading…</p>;
  const all = [...data.services, ...data.bundles.map((b) => ({ ...b, isBundle: true }))];

  return (
    <div data-testid="admin-services-tab" className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {all.map((s) => <ServiceCard key={s.code} s={s} onSave={save} />)}
    </div>
  );
}

function ServiceCard({ s, onSave }) {
  const [price, setPrice] = useState(s.price);
  return (
    <div className="rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
      <div className="flex items-center justify-between">
        <h3 className="font-extrabold text-brand-green">{s.name}</h3>
        {s.isBundle && <span className="rounded-full bg-brand-yellow px-3 py-0.5 text-xs font-extrabold text-brand-green">Bundle</span>}
      </div>
      <p className="mt-1 text-xs text-slate-500">per {s.unit}</p>
      <div className="mt-3 flex items-center gap-2">
        <span className="font-bold text-slate-500">$</span>
        <input data-testid={`price-input-${s.code}`} type="number" step="0.05" min="0" className={`${input} w-24`}
          value={price} onChange={(e) => setPrice(e.target.value)} />
        <button data-testid={`save-price-${s.code}`} onClick={() => onSave(s.code, price, true)}
          className="btn-bounce rounded-full bg-brand-green px-4 py-2 text-xs font-extrabold text-white">Save</button>
      </div>
    </div>
  );
}

function SlotsTab() {
  const [slots, setSlots] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [newSlot, setNewSlot] = useState("");
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");

  const load = () => api.get("/admin/settings").then((r) => { setSlots(r.data.slots); setBlackouts(r.data.blackout_dates); });
  useEffect(() => { load(); }, []);

  const saveSlots = async (updated) => {
    try {
      await api.put("/admin/timeslots", { slots: updated });
      setSlots(updated);
      toast.success("Time slots updated");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addBlackout = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/blackouts", { date: newDate, reason });
      setNewDate(""); setReason("");
      toast.success("Blackout date added");
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="admin-slots-tab" className="grid gap-8 lg:grid-cols-2">
      <div className="rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
        <h3 className="font-extrabold text-brand-green">Pick-up time slots</h3>
        <div className="mt-4 space-y-2">
          {slots.map((s, i) => (
            <div key={s} className="flex items-center justify-between rounded-xl bg-brand-cream px-4 py-2.5 text-sm font-bold">
              {s}
              <button data-testid={`delete-slot-${i}`} onClick={() => saveSlots(slots.filter((x) => x !== s))}
                className="text-red-400 hover:text-red-600" aria-label="Delete slot"><Trash size={16} weight="duotone" /></button>
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <input data-testid="new-slot-input" className={`${input} flex-1`} placeholder="e.g. 7:00 PM - 9:00 PM" value={newSlot} onChange={(e) => setNewSlot(e.target.value)} />
          <button data-testid="add-slot-btn" onClick={() => { if (newSlot.trim()) { saveSlots([...slots, newSlot.trim()]); setNewSlot(""); } }}
            className="btn-bounce rounded-full bg-brand-green px-5 py-2 text-sm font-extrabold text-white">Add</button>
        </div>
      </div>
      <div className="rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
        <h3 className="font-extrabold text-brand-green">Blackout dates (no pick ups)</h3>
        <div className="mt-4 space-y-2">
          {blackouts.length === 0 && <p className="text-sm text-slate-400">No blackout dates set.</p>}
          {blackouts.map((b) => (
            <div key={b.date} data-testid={`blackout-${b.date}`} className="flex items-center justify-between rounded-xl bg-red-50 px-4 py-2.5 text-sm font-bold text-red-700">
              {b.date} {b.reason && <span className="text-xs font-normal">({b.reason})</span>}
              <button data-testid={`delete-blackout-${b.date}`} onClick={async () => { await api.delete(`/admin/blackouts/${b.date}`); load(); }}
                className="text-red-400 hover:text-red-600" aria-label="Remove blackout"><Trash size={16} weight="duotone" /></button>
            </div>
          ))}
        </div>
        <form onSubmit={addBlackout} className="mt-4 flex flex-wrap gap-2">
          <input data-testid="blackout-date-input" type="date" required className={input} value={newDate} onChange={(e) => setNewDate(e.target.value)} />
          <input data-testid="blackout-reason-input" className={`${input} flex-1`} placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <button type="submit" data-testid="add-blackout-btn" className="btn-bounce rounded-full bg-brand-green px-5 py-2 text-sm font-extrabold text-white">Add</button>
        </form>
      </div>
    </div>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <p className="font-bold text-slate-400">Loading…</p>;
  const maxWeek = Math.max(1, ...stats.weekly.map((w) => w.count));
  const maxPop = Math.max(1, ...stats.popular_services.map((p) => p.count));

  return (
    <div data-testid="admin-stats-tab">
      <div className="grid gap-4 sm:grid-cols-3">
        {[["Total bookings", stats.total_bookings], ["This week", stats.bookings_this_week], ["Revenue (est.)", `$${stats.revenue.toFixed(2)}`]].map(([l, v]) => (
          <div key={l} data-testid={`stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
            <p className="text-sm font-bold text-slate-500">{l}</p>
            <p className="mt-1 text-3xl font-black text-brand-green">{v}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <div className="rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
          <h3 className="font-extrabold text-brand-green">Bookings per week</h3>
          <div className="mt-4 space-y-3">
            {stats.weekly.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
            {stats.weekly.map((w) => (
              <div key={w.week} className="flex items-center gap-3 text-sm">
                <span className="w-16 font-bold text-slate-500">{w.week}</span>
                <div className="h-6 rounded-full bg-brand-green" style={{ width: `${(w.count / maxWeek) * 70}%`, minWidth: "1.5rem" }} />
                <span className="font-black text-brand-green">{w.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-3xl bg-white p-6 shadow-[0_8px_30px_rgb(27,94,32,0.08)]">
          <h3 className="font-extrabold text-brand-green">Most popular services</h3>
          <div className="mt-4 space-y-3">
            {stats.popular_services.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
            {stats.popular_services.map((p) => (
              <div key={p.service} className="flex items-center gap-3 text-sm">
                <span className="w-16 font-bold capitalize text-slate-500">{p.service}</span>
                <div className="h-6 rounded-full bg-brand-yellow" style={{ width: `${(p.count / maxPop) * 70}%`, minWidth: "1.5rem" }} />
                <span className="font-black text-brand-green">{p.count}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {Object.entries(stats.status_counts).map(([s, c]) => (
              <span key={s} className="rounded-full bg-soft-green px-3 py-1 text-xs font-extrabold text-brand-green">{s}: {c}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
